import { useMemo, useState, useEffect, useId } from 'react'

import {
  Badge,
  Button,
  type CheckedState,
  Dialog,
  Form,
  Input,
  VisuallyHidden,
  Text,
  CriticalIcon,
} from '@buffer-mono/popcorn'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import type { ChannelGroup_ChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'
import { useCreateChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useCreateChannelGroup'
import { useUpdateChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useUpdateChannelGroup'

import { ChannelSelection } from './ChannelSelection'
import { useChannels } from '../PublishSidebar/useChannels'

import styles from './ChannelGroupDialog.module.css'

export type ChannelGroupDialogMode = 'create' | 'edit'

const NAME_MIN_LENGTH = 3
const NAME_MAX_LENGTH = 50

function ChannelGroupDialog({
  channelGroup,
  isOpen,
  onOpenChange,
  onClose,
  isMobile,
}: {
  channelGroup?: ChannelGroup_ChannelGroup
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose?: () => void
  isMobile: boolean
}): JSX.Element {
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const { channels } = useChannels()

  const { createChannelGroup, loading: createChannelGroupLoading } =
    useCreateChannelGroup()

  const { updateChannelGroup, loading: updateChannelGroupLoading } =
    useUpdateChannelGroup()

  const isLoading = createChannelGroupLoading || updateChannelGroupLoading

  const isEditMode = !!channelGroup

  // Edit mode: pre-set the name, channels and set dialog copy
  const preSelectedChannelIds = useMemo(
    () =>
      isEditMode
        ? channelGroup?.channels?.map((channel) => channel.id) ?? []
        : [],
    [isEditMode, channelGroup],
  )
  const preSetName = isEditMode ? channelGroup?.name ?? null : null
  const title = isEditMode ? 'Edit channel group' : 'New channel group'
  const description = isEditMode
    ? 'Edit your existing channel group, optionally change the name and add or remove channels to the group.'
    : 'Create a new channel group, with a custom name and add channels to it.'
  const nameLengthError = `Name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters`

  const [name, setName] = useState<string | null>(preSetName)
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>(
    preSelectedChannelIds,
  )

  const nameInputId = useId()
  const [nameError, setNameError] = useState<string | null>(null)
  const [channelError, setChannelError] = useState<string | null>(null)

  // Set prefilled group data
  useEffect(() => {
    if (isEditMode && channelGroup) {
      setName(channelGroup.name ?? null)
      setSelectedChannelIds(
        channelGroup.channels?.map((channel) => channel.id) ?? [],
      )
    } else {
      // Reset state for create mode
      setName(null)
      setSelectedChannelIds([])
    }
  }, [isEditMode, channelGroup])

  const handleFilter = (
    value: string,
    search: string,
    keywords?: string[],
  ): number => {
    if (!search) return 1
    return keywords?.some((keyword) =>
      keyword.toLowerCase().includes(search.toLowerCase()),
    )
      ? 1
      : 0
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value)
    if (e.target.value.length > NAME_MAX_LENGTH) {
      setNameError(nameLengthError)
    } else {
      setNameError(null)
    }
  }

  const handleOnSelectChannel = (
    channelId: string,
    checked: CheckedState,
  ): void => {
    if (checked) {
      setSelectedChannelIds([...selectedChannelIds, channelId])
    } else {
      setSelectedChannelIds(selectedChannelIds.filter((id) => id !== channelId))
    }
    setChannelError(null)
  }

  const handleClose = (): void => {
    setNameError(null)
    setChannelError(null)
    onOpenChange(false)
    setName(null)
    setSelectedChannelIds([]) // reset the selected channels
    onClose?.()
  }

  const handleFormValidation = (): boolean => {
    let isValid = true
    // Name validation
    const isValidName =
      name !== null &&
      name.length >= NAME_MIN_LENGTH &&
      name.length <= NAME_MAX_LENGTH
    if (!isValidName) {
      setNameError(nameLengthError)
      isValid = false
    } else {
      setNameError(null)
    }

    // Channel validation
    if (selectedChannelIds.length === 0) {
      setChannelError('Please select at least one channel')
      isValid = false
    } else {
      setChannelError(null)
    }

    return isValid
  }

  const handleSaveChanges = async (): Promise<void> => {
    const isValid = handleFormValidation()
    if (!isValid || !channelGroup?.id || !name) return

    await updateChannelGroup({
      channelGroupId: String(channelGroup.id),
      name,
      channelIds: selectedChannelIds,
    }).then(() => {
      handleClose()
      return undefined
    })
  }

  const handleCreateNewGroup = async (): Promise<void> => {
    const isValid = handleFormValidation()
    if (!isValid || !name || !accountId || !organizationId) return

    const channelIds =
      selectedChannelIds.length > 0 ? selectedChannelIds : undefined
    await createChannelGroup({
      name,
      channelIds,
      accountId,
      organizationId,
    }).then(() => {
      handleClose()
      return undefined
    })
  }

  const onDialogOpenChange = (open: boolean): void => {
    if (!open) handleClose()
    onOpenChange(open)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onDialogOpenChange}>
      <Dialog.Content
        className={styles.content}
        size={isMobile ? 'maximize' : undefined}
      >
        <Dialog.Header>
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          <Dialog.CloseButton onClick={handleClose} />
          <VisuallyHidden>
            <Dialog.Description>{description}</Dialog.Description>
          </VisuallyHidden>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          <Form className={styles.form}>
            <Form.Field name="group-name" className={styles.bodyAlignment}>
              <Form.Label>Name</Form.Label>
              <Form.Control>
                <Input
                  id={nameInputId}
                  name="group-name"
                  value={name ?? ''}
                  type="text"
                  placeholder="Add channel group name"
                  onChange={handleNameChange}
                  aria-invalid={!!nameError}
                  aria-describedby={
                    nameError ? `${nameInputId}:error` : undefined
                  }
                  size="large"
                  // TODO: we will reuse this when we return to implement the avatar/emoji feature
                  // prefix={
                  //   <IconButton label="Add emoji" variant="tertiary">
                  //     <SmileIcon />
                  //   </IconButton>
                  // }
                />
              </Form.Control>
              {nameError && (
                <Text
                  as="span"
                  size="sm"
                  id={`${nameInputId}:error`}
                  color="critical"
                  aria-live="polite"
                  role="alert"
                  className={styles.textError}
                >
                  <CriticalIcon size="xsmall" /> {nameError}
                </Text>
              )}
            </Form.Field>
            <Form.Field name="group-channels">
              <Form.Label className={styles.bodyAlignment}>
                Channels{' '}
                <Badge variant="neutral" size="xsmall">
                  {selectedChannelIds.length}
                </Badge>
                <VisuallyHidden>channels selected</VisuallyHidden>
              </Form.Label>
              <Form.Control>
                <ChannelSelection
                  filter={handleFilter}
                  channels={channels}
                  selectedChannelIds={selectedChannelIds}
                  handleOnSelectChannel={handleOnSelectChannel}
                  errorMessage={channelError}
                />
              </Form.Control>
            </Form.Field>
          </Form>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" size="large" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={(): void => {
              if (channelGroup) {
                void handleSaveChanges()
              } else {
                void handleCreateNewGroup()
              }
            }}
            loading={isLoading}
            disabled={isLoading}
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export { ChannelGroupDialog }
